import React, { useState, useEffect } from 'react';
import TagPicker from './TagPicker';
import { Tag } from '../../types';
import { tagService } from '../../services';

export interface SetupWizardResult {
  name: string;
  description: string;
  tagIds: string[];
  expectedDurationDays?: number;
  requiresAsset?: boolean;
  assetType?: string | null;
  eventCategory?: string; // Event type or 'specific_date'
  eventTiming?: 'pre' | 'post';
}

interface SetupWizardModalProps {
  type: 'dashboard' | 'workflow';
  onComplete: (result: SetupWizardResult) => void;
  onCancel: () => void;
}

const EQUITY_EVENT_CATEGORIES = [
  { value: 'earnings', label: 'Earnings' },
  { value: 'investor_relations_day', label: 'Investor Relations Day' },
  { value: 'dividend_ex_date', label: 'Dividend Ex-Date' },
  { value: 'conference', label: 'Conference' },
  { value: 'ipo', label: 'IPO' },
  { value: 'news_alert', label: 'News Alert' },
  { value: 'custom', label: 'Custom' },
];

const ASSET_TYPES = [
  { value: '', label: 'None', enabled: true, description: 'General workflow' },
  { value: 'equity', label: 'Equity', enabled: true, description: 'Stock / equity research' },
  { value: 'bond', label: 'Bond', enabled: false, description: 'Coming soon' },
  { value: 'commodity', label: 'Commodity', enabled: false, description: 'Coming soon' },
  { value: 'currency', label: 'Currency', enabled: false, description: 'Coming soon' },
];

const SetupWizardModal: React.FC<SetupWizardModalProps> = ({
  type,
  onComplete,
  onCancel,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [expectedDurationDays, setExpectedDurationDays] = useState<number | undefined>(undefined);
  const [assetType, setAssetType] = useState<string>('');
  const [eventMode, setEventMode] = useState<'none' | 'date' | 'category'>('none');
  const [eventCategory, setEventCategory] = useState('');
  const [eventTiming, setEventTiming] = useState<'pre' | 'post' | ''>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadTags = async () => {
    try {
      const data = await tagService.list();
      setTags(data);
    } catch (err) {
      console.error('Failed to load tags', err);
    }
  };

  useEffect(() => { loadTags(); }, []);

  const label = type === 'dashboard' ? 'Dashboard' : 'Workflow';
  const clearErrors = () => { if (Object.keys(errors).length > 0) setErrors({}); };

  const handleAssetTypeChange = (value: string) => {
    setAssetType(value);
    if (!value) {
      setEventMode('none');
      setEventCategory('');
      setEventTiming('');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = `${label} name is required`;
    if (!description.trim()) newErrors.description = 'Description is required';
    if (tagIds.length === 0) newErrors.tagIds = 'At least one tag is required';
    if (type === 'workflow' && (!expectedDurationDays || expectedDurationDays <= 0)) {
      newErrors.duration = 'Expected duration is required';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    onComplete({
      name: name.trim(),
      description: description.trim(),
      tagIds,
      ...(type === 'workflow' && {
        expectedDurationDays,
        requiresAsset: !!assetType,
        assetType: assetType || null,
        ...(eventMode === 'date' ? { eventCategory: 'specific_date' } : {}),
        ...(eventMode === 'category' && eventCategory ? { eventCategory, eventTiming: eventTiming || undefined } : {}),
      }),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden border border-neutral-200">
        {/* Header */}
        <div className="px-7 pt-7 pb-2">
          <h2 className="text-xl font-bold text-neutral-900 tracking-tight">
            New {label}
          </h2>
          <p className="text-sm text-neutral-400 mt-1">
            Configure the basics — everything can be changed later.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-7 pb-7 pt-4">
          {/* Name + Duration side by side */}
          <div className={`flex gap-3 ${type === 'workflow' ? '' : ''}`}>
            <div className="flex-1">
              <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">
                Name <span className="text-danger-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={e => { setName(e.target.value); clearErrors(); }}
                className={`w-full px-3 py-2.5 text-sm border rounded-lg transition-colors ${
                  errors.name ? 'border-danger-400' : 'border-neutral-200 hover:border-neutral-300'
                } focus:border-neutral-400 focus:outline-none`}
                placeholder={`e.g. Q1 Earnings Review`}
                autoFocus
              />
              {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
            </div>
            {type === 'workflow' && (
              <div className="w-28 flex-shrink-0">
                <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">
                  Duration <span className="text-danger-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={expectedDurationDays || ''}
                    onChange={e => {
                      setExpectedDurationDays(e.target.value ? parseInt(e.target.value, 10) : undefined);
                      clearErrors();
                    }}
                    className={`w-full px-3 py-2.5 pr-12 text-sm border rounded-lg transition-colors ${
                      errors.duration ? 'border-danger-400' : 'border-neutral-200 hover:border-neutral-300'
                    } focus:border-neutral-400 focus:outline-none`}
                    placeholder="—"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-400 pointer-events-none">days</span>
                </div>
                {errors.duration && <p className="text-xs text-danger-500 mt-1">{errors.duration}</p>}
              </div>
            )}
          </div>

          {/* Description */}
          <div className="mt-3">
            <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">
              Description <span className="text-danger-500">*</span>
            </label>
            <textarea
              value={description}
              onChange={e => { setDescription(e.target.value); clearErrors(); }}
              rows={2}
              className={`w-full px-3 py-2.5 text-sm border rounded-lg resize-none transition-colors ${
                errors.description ? 'border-danger-400' : 'border-neutral-200 hover:border-neutral-300'
              } focus:border-neutral-400 focus:outline-none`}
              placeholder={`What is this ${type} for?`}
            />
            {errors.description && <p className="text-xs text-danger-500 mt-1">{errors.description}</p>}
          </div>

          {/* Tags */}
          <div className="mt-3">
            <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-1.5">
              Tags <span className="text-danger-500">*</span>
            </label>
            <TagPicker
              value={tagIds}
              onChange={(ids) => { setTagIds(ids); clearErrors(); }}
              tags={tags}
              onTagsChanged={loadTags}
              placeholder="Search or create tags..."
            />
            {errors.tagIds && <p className="text-xs text-danger-500 mt-1">{errors.tagIds}</p>}
          </div>

          {/* Workflow-specific: Asset Type + Event Date */}
          {type === 'workflow' && (
            <>
              {/* Divider */}
              <div className="my-5 border-t border-neutral-100" />

              {/* Asset Type */}
              <div>
                <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-2.5">
                  Asset Class
                </label>
                <div className="grid grid-cols-5 gap-1.5">
                  {ASSET_TYPES.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      disabled={!opt.enabled}
                      onClick={() => opt.enabled && handleAssetTypeChange(opt.value)}
                      className={`relative px-2 py-2.5 text-xs font-medium rounded-lg border transition-all text-center ${
                        !opt.enabled
                          ? 'border-neutral-100 text-neutral-300 cursor-not-allowed bg-neutral-50'
                          : assetType === opt.value
                            ? 'border-primary-400 bg-primary-50 text-primary-700 shadow-sm'
                            : 'border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                      }`}
                    >
                      {opt.label}
                      {!opt.enabled && (
                        <span className="block text-[9px] text-neutral-300 font-normal mt-0.5">soon</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Event — only shown when an asset type is selected */}
              {assetType && (
                <div className="mt-5">
                  <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-2.5">
                    Event
                  </label>
                  {/* Mode toggle */}
                  <div className="grid grid-cols-3 gap-1.5 mb-3">
                    {([
                      { value: 'none', label: 'None' },
                      { value: 'date', label: 'Specific Date' },
                      { value: 'category', label: 'Pre / Post Event' },
                    ] as const).map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setEventMode(opt.value);
                          if (opt.value !== 'category') { setEventCategory(''); setEventTiming(''); }
                        }}
                        className={`px-2 py-2.5 text-xs font-medium rounded-lg border transition-all text-center ${
                          eventMode === opt.value
                            ? 'border-primary-400 bg-primary-50 text-primary-700 shadow-sm'
                            : 'border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Category grid + pre/post */}
                  {eventMode === 'category' && (
                    <>
                      <div className="grid grid-cols-3 gap-1.5">
                        {EQUITY_EVENT_CATEGORIES.map((cat) => (
                          <button
                            key={cat.value}
                            type="button"
                            onClick={() => {
                              if (eventCategory === cat.value) {
                                setEventCategory('');
                                setEventTiming('');
                              } else {
                                setEventCategory(cat.value);
                                if (!eventTiming) setEventTiming('pre');
                              }
                            }}
                            className={`px-3 py-2.5 text-xs font-medium rounded-lg border transition-all text-center ${
                              eventCategory === cat.value
                                ? 'border-primary-400 bg-primary-50 text-primary-700 shadow-sm'
                                : 'border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50'
                            }`}
                          >
                            {cat.label}
                          </button>
                        ))}
                      </div>

                      {/* Pre / Post toggle */}
                      {eventCategory && (
                        <div className="mt-3">
                          <label className="block text-[11px] font-semibold text-neutral-400 uppercase tracking-wider mb-2">
                            Timing
                          </label>
                          <div className="grid grid-cols-2 gap-1.5">
                            {([
                              { value: 'pre', label: 'Pre-Event', desc: 'Next upcoming' },
                              { value: 'post', label: 'Post-Event', desc: 'Most recent past' },
                            ] as const).map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setEventTiming(opt.value)}
                                className={`px-3 py-2.5 rounded-lg border transition-all text-center ${
                                  eventTiming === opt.value
                                    ? 'border-primary-400 bg-primary-50 shadow-sm'
                                    : 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
                                }`}
                              >
                                <span className={`block text-xs font-medium ${eventTiming === opt.value ? 'text-primary-700' : 'text-neutral-600'}`}>
                                  {opt.label}
                                </span>
                                <span className={`block text-[10px] mt-0.5 ${eventTiming === opt.value ? 'text-primary-500' : 'text-neutral-400'}`}>
                                  {opt.desc}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <p className="text-[11px] text-neutral-400 mt-2">
                    {eventMode === 'none' && 'No event — this workflow is not anchored to a calendar event'}
                    {eventMode === 'date' && 'The event date will be provided when assigning the workflow'}
                    {eventMode === 'category' && !eventCategory && 'Pick an event type, then choose pre or post'}
                    {eventMode === 'category' && eventCategory && eventTiming === 'pre' && `Anchored before the next ${eventCategory.replace(/_/g, ' ')}`}
                    {eventMode === 'category' && eventCategory && eventTiming === 'post' && `Anchored after the last ${eventCategory.replace(/_/g, ' ')}`}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-7 pt-5 border-t border-neutral-100">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 text-sm font-medium text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
            >
              Create {label}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetupWizardModal;
